import { api } from '@/lib/axios';

interface CropData {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface PhotoCropResponse {
  id: string;
  blob_id: string;
  item_id: string;
  crop_data: CropData | null;
  status: 'raw' | 'processing' | 'ready' | 'error';
  processed_blob_id: string | null;
  optimization_status: string;
  processed_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Update item photo crop data
 * Sends crop coordinates and viewport position to backend for processing
 */
export async function updateItemPhotoCrop(
  itemId: string,
  attachmentId: string,
  cropData: CropData,
  viewportPosition: { x: number; y: number }
): Promise<PhotoCropResponse> {
  const response = await api.patch(
    `/v1/admin/items/${itemId}/photos/${attachmentId}/crop`,
    {
      crop_data: cropData,
      viewport_position: viewportPosition,
    }
  );

  return response.data;
}

/**
 * Activa o desactiva la postedición de una foto de plato (spec 011).
 * attachmentId es el id del metadato de recorte, como en updateItemPhotoCrop.
 */
export async function updateItemPhotoEnhancement(itemId: string, attachmentId: string, enabled: boolean): Promise<PhotoCropResponse> {
  const response = await api.patch(`/v1/admin/items/${itemId}/photos/${attachmentId}/enhancement`, { enabled });
  return response.data;
}

/**
 * Poll for item photo processing status
 * Returns when status changes from processing to ready or error
 */
export async function pollItemPhotoStatus(
  itemId: string,
  attachmentId: string,
  maxAttempts: number = 30,
  intervalMs: number = 1000
): Promise<PhotoCropResponse> {
  let attempts = 0;

  while (attempts < maxAttempts) {
    // We would need to add a GET endpoint to fetch status
    // For now, we'll assume the response from update is sufficient
    // In production, you'd want a dedicated status endpoint

    attempts++;
    if (attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  throw new Error('Item photo processing timeout');
}
